import PropTypes from 'prop-types';

import Field, { useFieldIds } from './Field.jsx';

import './ColorPicker.css';

/** The theme's visual palette, in order. Ten is all there is, so ten is the ceiling. */
export const MAX_COLORS = 10;
export const COLOR_TOKENS = Array.from({ length: MAX_COLORS }, (_, i) => `--visual-color-${i + 1}`);

/**
 * A row of colour swatches — pick one.
 *
 * The value is the design token (`--visual-color-3`), not a hex, so a chosen colour follows the
 * theme instead of freezing one theme's value into stored data.
 */
const ColorPicker = ({
    id,
    label,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    tooltip,
    className = '',
    count = 8,
    value,
    onChange,
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);
    const tokens = COLOR_TOKENS.slice(0, Math.min(Math.max(count, 1), MAX_COLORS));

    return (
        <Field
            id={fieldId}
            label={label}
            helpText={helpText}
            status={status}
            isRequired={isRequired}
            isDisabled={isDisabled}
            tooltip={tooltip}
            className={className}
        >
            <div id={fieldId} className="color-picker" role="radiogroup" aria-describedby={messageId}>
                {tokens.map((token) => (
                    <button
                        key={token}
                        type="button"
                        role="radio"
                        aria-checked={value === token}
                        aria-label={token.replace('--visual-color-', 'Colour ')}
                        className={`color-picker-swatch ${value === token ? 'is-selected' : ''}`}
                        style={{ background: `var(${token})` }}
                        disabled={isDisabled}
                        onClick={() => onChange?.(token)}
                    />
                ))}
            </div>
        </Field>
    );
};

ColorPicker.propTypes = {
    id: PropTypes.string,
    label: PropTypes.string,
    helpText: PropTypes.string,
    status: PropTypes.oneOf(['neutral', 'danger']),
    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,
    tooltip: PropTypes.string,
    className: PropTypes.string,
    count: PropTypes.number,
    value: PropTypes.string,
    onChange: PropTypes.func,
};

export default ColorPicker;
